import React, { useState } from 'react';
import { DateRangePicker } from '../dashboard/DateRangePicker';
import { GranularitySelector, Granularity } from '../dashboard/GranularitySelector';
import { DisputesFilters } from './DisputesFilters';
import { EmptyState } from './EmptyState';
import { ChevronDownIcon } from 'lucide-react';
interface DateRange {
  start: Date;
  end: Date;
}
interface DisputesTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function DisputesTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: DisputesTabProps) {
  // New filter states
  const [disputeDateBasis, setDisputeDateBasis] = useState<'dispute-date' | 'payment-date'>('dispute-date');
  const [paymentCountBasis, setPaymentCountBasis] = useState<'payment-count' | 'payment-volume'>('payment-count');
  const [includeEFW, setIncludeEFW] = useState<'include' | 'exclude'>('include');
  // Existing filter states
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDisputeDateBasis('dispute-date');
    setPaymentCountBasis('payment-count');
    setIncludeEFW('include');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-815">
      {/* All Filters - Compact Layout */}
      <div className="space-y-3" data-id="element-816">
        {/* First Row: Date Range and Granularity */}
        <div className="flex flex-wrap items-center gap-3" data-id="element-817">
          <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-818" />
          <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-819" />
        </div>

        {/* Disputes Filters (includes both rows) */}
        <DisputesFilters disputeDateBasis={disputeDateBasis} onDisputeDateBasisChange={setDisputeDateBasis} paymentCountBasis={paymentCountBasis} onPaymentCountBasisChange={setPaymentCountBasis} includeEFW={includeEFW} onIncludeEFWChange={setIncludeEFW} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-820" />
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-821">
        <div className="p-6 border-b border-gray-200" data-id="element-822">
          <div className="flex items-center justify-between" data-id="element-823">
            <div className="flex items-center gap-4" data-id="element-824">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-825">
                Key metrics for cards
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-826">
                <span data-id="element-827">Compared to</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-828">
                  Previous period
                  <ChevronDownIcon size={16} data-id="element-829" />
                </button>
                <span className="text-gray-500" data-id="element-830">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-831">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-833">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-832" />
        </div>
      </div>

      {/* Card disputes breakdown */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-834">
        <div className="p-6 border-b border-gray-200" data-id="element-835">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-836">
            Card disputes breakdown
          </h2>
        </div>

        <div className="p-12" data-id="element-837">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-839">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-838" />
        </div>
      </div>

      {/* Monthly submission performance */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-840">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-841">
          <div data-id="element-842">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-843">
              Monthly submission performance
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-844">
              A detailed view of submission performance metrics and trends over
              the past 12 months.
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-845">
            All reasons
            <ChevronDownIcon size={16} data-id="element-846" />
          </button>
        </div>

        <div className="p-12" data-id="element-847">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-849">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-848" />
        </div>
      </div>

      {/* Monthly dispute outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-850">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-851">
          <div data-id="element-852">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-853">
              Monthly dispute outcomes
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-854">
              A breakdown of how card disputes were resolved over the past 12
              months.
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-855">
            All reasons
            <ChevronDownIcon size={16} data-id="element-856" />
          </button>
        </div>

        <div className="p-12" data-id="element-857">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-859">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-858" />
        </div>
      </div>
    </div>;
}